import { c as _c } from "react/compiler-runtime";
import type { FetchChatTranslateParams } from '@module/apis/client-side/chat/type';
import { cn } from '@module/lib/utils';
import type { NormalChatMessage } from '@module/types/centrifuge/public-message';
import { Icon } from '../icons';
import AutoChatTranslate from './AutoChatTranslate';
import ChatTranslateButton from './ChatTranslateButton';
import ChatTranslateResult from './ChatTranslateResult';
import { getTranslateErrorText } from './getTranslateErrorText';
import useChatTranslateController from './useChatTranslateController';
interface Props {
  chatData: NormalChatMessage;
  isMessageTranslateEnabled: boolean;
  // 해당 방송의 bj(시청자 본인 == BJ)인지 여부
  isRoomMaster?: boolean;
  filteredMessage?: string;
  onError: ((error: Error, variables: FetchChatTranslateParams, context: unknown) => Promise<unknown> | unknown) | undefined;
  onClickWithoutLogin: () => void;
  triggerClassName?: string; // trigger(버튼/자동 인디케이터)를 감싸는 래퍼 div
  buttonClassName?: string; // 수동 번역 버튼 className
  autoClassName?: string; // 자동 번역 pending 인디케이터 className
  resultClassName?: string; // 번역 결과 블록 className
}

/**
 * pandatv(pc/mobile) 전용 채팅 번역 UI 조합.
 * "BJ 자동 번역 vs 수동 버튼" 판정(useChatTranslateController) + 트리거(블록 배치) + 번역 라벨/결과를
 * 한 곳에 조립해, NormalChat이 트리거 분기·결과 블록을 직접 들고 있지 않게 한다.
 *
 * 왜 pandatv 전용인가: fanza는 '자동 번역' 기능이 현재 제공되지 않는다.
 * 그리고 fanza는 트리거를 메시지 <p> 안 '인라인'에 두기 때문에 '블록 배치 + 라벨' 조합을 쓰지 못한다.
 * fanza는 원자(ChatTranslateButton/ChatTranslateResult)를
 * 직접 조합하고, 공유되는 것은 로직 훅(useChatTranslateController)과 원자들뿐이다.
 */
function ChatTranslatePanel(t0) {
  const $ = _c(41);
  const {
    chatData,
    isMessageTranslateEnabled,
    isRoomMaster,
    filteredMessage,
    onError,
    onClickWithoutLogin,
    triggerClassName,
    buttonClassName,
    autoClassName,
    resultClassName
  } = t0;
  let t1;
  if ($[0] !== chatData || $[1] !== isRoomMaster) {
    t1 = {
      chatData,
      isRoomMaster,
      autoEnabled: true
    };
    $[0] = chatData;
    $[1] = isRoomMaster;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    translatedMessage,
    setTranslatedMessage,
    isAuto,
    hasError,
    setHasError
  } = useChatTranslateController(t1);
  if (!isMessageTranslateEnabled) {
    return null;
  }
  const t2 = chatData.data?.filtered;
  let t3;
  if ($[3] !== onError || $[4] !== setHasError) {
    t3 = (error, variables, context) => {
      setHasError(true);
      return onError?.(error, variables, context);
    };
    $[3] = onError;
    $[4] = setHasError;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== chatData || $[7] !== filteredMessage || $[8] !== isRoomMaster || $[9] !== setTranslatedMessage || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      chatData,
      isRoomMaster,
      filtered: t2,
      filteredMessage,
      setTranslatedMessage,
      onError: t3
    };
    $[6] = chatData;
    $[7] = filteredMessage;
    $[8] = isRoomMaster;
    $[9] = setTranslatedMessage;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const shared = t4;
  if (translatedMessage) {
    let t5;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex items-center gap-1 text-[12px] text-colorFg16"><Icon eager={true} name="TranslateFilled" size={12} />번역</div>;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[14] !== resultClassName || $[15] !== translatedMessage) {
      t6 = <>{t5}<ChatTranslateResult marker={"\u3134"} messageClassName="break-all" className={resultClassName} message={translatedMessage} /></>;
      $[14] = resultClassName;
      $[15] = translatedMessage;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    return t6;
  }
  if (hasError) {
    let t5;
    if ($[17] !== buttonClassName || $[18] !== onClickWithoutLogin || $[19] !== shared) {
      t5 = <ChatTranslateButton {...shared} className={buttonClassName} onClickWithoutLogin={onClickWithoutLogin} />;
      $[17] = buttonClassName;
      $[18] = onClickWithoutLogin;
      $[19] = shared;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    let t6;
    if ($[21] !== t5 || $[22] !== triggerClassName) {
      t6 = <div className={triggerClassName}>{t5}</div>;
      $[21] = t5;
      $[22] = triggerClassName;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    let t7;
    if ($[24] !== resultClassName) {
      t7 = cn(resultClassName, "text-system-red1");
      $[24] = resultClassName;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    let t8;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = getTranslateErrorText();
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    let t9;
    if ($[27] !== t7) {
      t9 = <ChatTranslateResult marker={"\u3134"} messageClassName="break-all" className={t7} message={t8} />;
      $[27] = t7;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    let t10;
    if ($[29] !== t6 || $[30] !== t9) {
      t10 = <>{t6}{t9}</>;
      $[29] = t6;
      $[30] = t9;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    return t10;
  }
  let t5;
  if ($[32] !== autoClassName || $[33] !== buttonClassName || $[34] !== isAuto || $[35] !== onClickWithoutLogin || $[36] !== shared) {
    t5 = isAuto ? <AutoChatTranslate {...shared} className={autoClassName} /> : <ChatTranslateButton {...shared} className={buttonClassName} onClickWithoutLogin={onClickWithoutLogin} />;
    $[32] = autoClassName;
    $[33] = buttonClassName;
    $[34] = isAuto;
    $[35] = onClickWithoutLogin;
    $[36] = shared;
    $[37] = t5;
  } else {
    t5 = $[37];
  }
  let t6;
  if ($[38] !== t5 || $[39] !== triggerClassName) {
    t6 = <div className={triggerClassName}>{t5}</div>;
    $[38] = t5;
    $[39] = triggerClassName;
    $[40] = t6;
  } else {
    t6 = $[40];
  }
  return t6;
}
export default ChatTranslatePanel;